import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, NgZone, OnDestroy } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter, Subscription } from 'rxjs';
import { environment } from 'src/environments/environment';
import { isEmbeddedFromParentOrigin } from './iframe-embed.util';

@Injectable({
  providedIn: 'root',
})
export class IframeHeightReporterService implements OnDestroy {
  private readonly parentOrigin = environment.iframeParentOrigin;
  private resizeObserver?: ResizeObserver;
  private routerSubscription?: Subscription;
  private animationFrameId?: number;
  private readonly windowLoadHandler = (): void => this.reportHeight();
  private readonly windowResizeHandler = (): void => this.reportHeight();
  private started = false;
  private lastHeight = 0;

  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly router: Router,
    private readonly ngZone: NgZone
  ) {}

  start(): void {
    if (this.started || !this.shouldInitializeReporting()) {
      return;
    }

    this.started = true;

    this.ngZone.runOutsideAngular(() => {
      this.reportHeight();

      if (typeof ResizeObserver !== 'undefined') {
        this.resizeObserver = new ResizeObserver(() => {
          this.reportHeight();
        });

        if (this.document.body) {
          this.resizeObserver.observe(this.document.body);
        }

        this.resizeObserver.observe(this.document.documentElement);
      }

      window.addEventListener('load', this.windowLoadHandler);
      window.addEventListener('resize', this.windowResizeHandler);

      if ('fonts' in this.document && this.document.fonts?.ready) {
        void this.document.fonts.ready.then(() => {
          this.reportHeight();
        });
      }
    });

    this.routerSubscription = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        queueMicrotask(() => {
          this.reportHeight();
        });
        requestAnimationFrame(() => {
          this.reportHeight();
        });
      });
  }

  stop(): void {
    if (!this.started) {
      return;
    }

    this.started = false;
    this.resizeObserver?.disconnect();
    this.resizeObserver = undefined;
    this.routerSubscription?.unsubscribe();
    this.routerSubscription = undefined;
    window.removeEventListener('load', this.windowLoadHandler);
    window.removeEventListener('resize', this.windowResizeHandler);

    if (this.animationFrameId !== undefined) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = undefined;
    }
  }

  ngOnDestroy(): void {
    this.stop();
  }

  private reportHeight(): void {
    if (!this.shouldReportToParent()) {
      return;
    }

    if (this.animationFrameId !== undefined) {
      cancelAnimationFrame(this.animationFrameId);
    }

    this.animationFrameId = requestAnimationFrame(() => {
      this.animationFrameId = undefined;

      const height = this.getDocumentHeightForParent();

      if (height === this.lastHeight) {
        return;
      }

      this.lastHeight = height;
      window.parent.postMessage(
        {
          type: 'IFRAME_HEIGHT_CHANGED',
          height,
        },
        this.parentOrigin
      );
    });
  }

  private shouldReportToParent(): boolean {
    return isEmbeddedFromParentOrigin();
  }

  private shouldInitializeReporting(): boolean {
    return this.shouldReportToParent();
  }

  /**
   * Parent sets iframe height from this value. After a tall resize, `documentElement.clientHeight`
   * and often `scrollHeight`/`offsetHeight` track that viewport, not short route content — so we
   * must not let those dominate when body metrics show a much smaller document.
   */
  private getDocumentHeightForParent(): number {
    const body = this.document.body;
    const html = this.document.documentElement;

    const bsh = body?.scrollHeight ?? 0;
    const boh = body?.offsetHeight ?? 0;
    const hch = html.clientHeight;
    const hsh = html.scrollHeight;
    const hoh = html.offsetHeight;

    const bodyMax = Math.max(bsh, boh);

    const htmlScrollEcho =
      hsh > 0 && hsh <= hch + 2 && bodyMax + 48 < hsh;
    const htmlOffsetEcho =
      hoh > 0 && hoh <= hch + 2 && bodyMax + 48 < hoh;

    const parts: number[] = [bodyMax, 1];
    if (!htmlScrollEcho) {
      parts.push(hsh);
    }
    if (!htmlOffsetEcho) {
      parts.push(hoh);
    }

    return Math.ceil(Math.max(...parts));
  }
}
